"use client";

import {
  Calculator,
  ChevronDown,
  ChevronRight,
  Copy,
  Hash,
  Settings2,
  ToggleLeft,
  Trash2,
  Type,
  Waves,
} from "lucide-react";
import { useCallback, type SyntheticEvent } from "react";

import { cn } from "@/lib/utils/cn";
import { Input, Select } from "@/components/ui-system";

import {
  allowedAggregationsForDataType,
  allowedGraphTypesForDataType,
  defaultGraphTypeForDataType,
  DATA_TYPE_OPTIONS,
  formatFormulaExpression,
  GRAPH_TYPE_OPTIONS,
  isNumericDataType,
  nextUniqueChannelKey,
  sanitizeAggregationsForDataType,
  sanitizeGraphTypeForDataType,
  type ChannelRow,
  type DataType,
} from "../../schema/datalogger.schema";
import { AggregatorSelect } from "./aggregator-select";
import { formatChannelThresholdSummary } from "./channel-display-utils";
import { GraphTypeSelector } from "./graph-type-selector";
import { ThresholdEditor } from "./threshold-editor";

const DATA_TYPE_ICONS: Record<DataType, typeof Waves> = {
  float: Waves,
  int: Hash,
  bool: ToggleLeft,
  string: Type,
  formula: Calculator,
};

const control =
  "ui-control h-10 min-h-10 w-full rounded-control border border-card-border bg-surface-primary text-caption shadow-none";

function ChannelKindBadge({ dataType }: { dataType: DataType }) {
  if (dataType === "formula") {
    return (
      <span className="status-pill status-pill-info !normal-case !tracking-normal">
        Formula
      </span>
    );
  }
  return (
    <span className="status-pill status-pill-neutral !normal-case !tracking-normal">
      Raw
    </span>
  );
}

function graphTypeLabel(value: ChannelRow["graphType"]): string {
  return GRAPH_TYPE_OPTIONS.find((option) => option.value === value)?.label ?? value.replace(/_/g, " ");
}

export type ChannelCardProps = {
  row: ChannelRow;
  index: number;
  peerKeys: string[];
  invalidFieldKey?: string | null;
  validationErrors?: string[];
  keyLocked?: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  advancedOpen: boolean;
  onToggleAdvanced: () => void;
  onUpdate: (updater: (row: ChannelRow) => ChannelRow) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  canRemove: boolean;
  onOpenThreshold: () => void;
  onOpenFormula: () => void;
  onOpenPrecision: () => void;
};

export function ChannelCard({
  row,
  index,
  peerKeys,
  invalidFieldKey = null,
  validationErrors = [],
  keyLocked = false,
  expanded,
  onToggleExpand,
  advancedOpen,
  onToggleAdvanced,
  onUpdate,
  onRemove,
  onDuplicate,
  canRemove,
  onOpenThreshold,
  onOpenFormula,
  onOpenPrecision,
}: ChannelCardProps) {
  const TypeIcon = DATA_TYPE_ICONS[row.dataType];
  const thresholdSummary = formatChannelThresholdSummary(row);
  const nameInvalid = invalidFieldKey === `channel.${index}.name`;
  const invalidControl =
    "!border-error !bg-error/5 !shadow-none !ring-0 focus:!border-error focus:!ring-0 focus-visible:!border-error focus-visible:!shadow-none";
  const fieldError = validationErrors[0] ?? null;

  const stopToggle = useCallback((e: SyntheticEvent) => {
    e.stopPropagation();
  }, []);
  const graphOptions = allowedGraphTypesForDataType(row.dataType);
  const aggregationOptions = allowedAggregationsForDataType(row.dataType);
  const showAggregatorSelector = row.dataType === "float" || row.dataType === "int" || row.dataType === "formula";

  return (
    <article
      className={cn(
        "overflow-hidden rounded-[20px] border border-card-border bg-card shadow-card transition-shadow duration-200",
        expanded && "shadow-card-hover ring-1 ring-primary/10",
      )}
    >
      {/* Header */}
      <div
        role="button"
        tabIndex={0}
        onClick={onToggleExpand}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggleExpand();
          }
        }}
        className="flex cursor-pointer items-center gap-ds-3 px-ds-4 py-ds-3 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--border-strong)]"
        aria-expanded={expanded}
      >
        <span
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted transition-transform duration-200 ease-out",
            expanded ? "rotate-0" : "-rotate-90",
          )}
          aria-hidden
        >
          <ChevronDown className="h-4 w-4" />
        </span>

        <div className="min-w-0 flex-1" onClick={stopToggle} onPointerDown={stopToggle}>
          <Input
            aria-label={`Channel ${index + 1} name`}
            className={cn(control, "!font-semibold tracking-tight", nameInvalid && invalidControl)}
            data-validation-key={`channel.${index}.name`}
            aria-invalid={nameInvalid || undefined}
            aria-describedby={nameInvalid ? `channel-${index}-name-error` : undefined}
            value={row.name}
            placeholder="Channel name"
            onChange={(e) => {
              const name = e.target.value;
              onUpdate((c) => ({
                ...c,
                name,
                channelKey: keyLocked ? c.channelKey : nextUniqueChannelKey(name, peerKeys),
              }));
            }}
          />
          {nameInvalid && fieldError ? (
            <p id={`channel-${index}-name-error`} className="mt-ds-1 text-caption text-error">
              {fieldError}
            </p>
          ) : null}
        </div>

        <div className="hidden shrink-0 items-center gap-ds-2 sm:flex" onClick={stopToggle} onPointerDown={stopToggle}>
          <ChannelKindBadge dataType={row.dataType} />
        </div>

        <div className="flex shrink-0 items-center gap-ds-1" onClick={stopToggle} onPointerDown={stopToggle}>
          <button
            type="button"
            className="rounded-lg p-ds-2 text-muted transition-colors duration-200 hover:bg-muted-bg/60 hover:text-foreground"
            title="Duplicate channel"
            aria-label="Duplicate channel"
            onClick={onDuplicate}
          >
            <Copy className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            disabled={!canRemove}
            className="rounded-lg p-ds-2 text-muted transition-colors duration-200 hover:bg-error/10 hover:text-error disabled:pointer-events-none disabled:opacity-40"
            title="Remove channel"
            aria-label="Remove channel"
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>

      <div className="flex border-t border-border/35 px-ds-4 py-ds-2 sm:hidden">
        <ChannelKindBadge dataType={row.dataType} />
      </div>

      {/* Peek line when collapsed */}
      {!expanded ? (
        <div className="border-t border-card-border bg-muted-bg/30 px-ds-4 py-ds-2 text-caption leading-relaxed text-muted dark:bg-muted-bg/10">
          <span className="text-foreground/80">{DATA_TYPE_OPTIONS.find((o) => o.value === row.dataType)?.label}</span>
          <span className="mx-1.5 text-border">·</span>
          <span>{graphTypeLabel(row.graphType)}</span>
          <span className="mx-1.5 text-border">·</span>
          <span>
            {row.aggregations.length} aggregation{row.aggregations.length === 1 ? "" : "s"}
          </span>
          {thresholdSummary ? (
            <>
              <span className="mx-1.5 text-border">·</span>
              <span className="line-clamp-1">{thresholdSummary}</span>
            </>
          ) : null}
        </div>
      ) : null}

      {/* Body */}
      {expanded ? (
        <div className="border-t border-card-border px-ds-4 pb-ds-4 pt-ds-4 transition-opacity duration-200 ease-out">
          <div className="grid gap-ds-6 lg:grid-cols-2 lg:gap-x-ds-8">
            {/* Left — Core identity */}
            <div className="space-y-ds-4">
              <div className="rounded-[20px] border border-card-border bg-muted-bg/30 p-ds-4">
                <h3 className="mb-ds-1 text-micro font-semibold uppercase tracking-[0.12em] text-muted">Core</h3>
                <p className="mb-ds-3 text-[11px] leading-5 text-muted">Set how this channel is named, formatted, and interpreted across the app.</p>
                <div className="space-y-ds-3">
                  <div>
                    <label className="mb-ds-1 block form-label">Unit</label>
                    <Input
                      className={control}
                      value={row.unit}
                      placeholder="e.g. °C, kW"
                      onChange={(e) => onUpdate((c) => ({ ...c, unit: e.target.value }))}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-ds-3">
                    <div>
                      <label className="mb-ds-1 block form-label">Prefix</label>
                      <Input
                        className={control}
                        value={row.prepend}
                        placeholder="e.g. $"
                        onChange={(e) => onUpdate((c) => ({ ...c, prepend: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="mb-ds-1 block form-label">Suffix</label>
                      <Input
                        className={control}
                        value={row.append}
                        placeholder="e.g. %"
                        onChange={(e) => onUpdate((c) => ({ ...c, append: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mb-ds-1 block form-label">Data type</label>
                    <div className="flex items-center gap-ds-2">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-control bg-surface-secondary text-foreground/80">
                        <TypeIcon className="h-4 w-4" aria-hidden />
                      </span>
                      <Select
                        aria-label="Data type"
                        className={cn(control, "min-w-0 flex-1")}
                          value={row.dataType}
                          onChange={(e) => {
                            const v = e.target.value as DataType;
                            onUpdate((c) => {
                              const next: ChannelRow = { ...c, dataType: v };
                              if (v !== "formula") next.formula = null;
                              else next.floatPrecision = 2;
                              if (!isNumericDataType(v)) {
                                next.minValue = null;
                                next.maxValue = null;
                                next.thresholdWarningLow = null;
                                next.thresholdWarningHigh = null;
                              }
                              next.aggregations = sanitizeAggregationsForDataType(v, next.aggregations);
                              next.graphType = sanitizeGraphTypeForDataType(v, next.graphType);
                              if (v === "bool" && next.boolExpected === "any") {
                                next.graphType = sanitizeGraphTypeForDataType(v, defaultGraphTypeForDataType(v));
                              }
                            return next;
                          });
                        }}
                      >
                        {DATA_TYPE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </Select>
                      {row.dataType === "float" || row.dataType === "formula" ? (
                        <button
                          type="button"
                          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-control text-muted ring-1 ring-border/50 transition-colors hover:bg-primary/10 hover:text-primary"
                          title={row.dataType === "float" ? "Decimal precision" : "Formula"}
                          aria-label={row.dataType === "float" ? "Decimal precision" : "Formula"}
                          onClick={row.dataType === "float" ? onOpenPrecision : onOpenFormula}
                        >
                          <Settings2 className="h-4 w-4" aria-hidden />
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right — Logic & behavior */}
            <div className="space-y-ds-4">
              <div className="rounded-[20px] border border-card-border bg-muted-bg/30 p-ds-4">
                <h3 className="mb-ds-1 text-micro font-semibold uppercase tracking-[0.12em] text-muted">Logic & behavior</h3>
                <p className="mb-ds-3 text-[11px] leading-5 text-muted">Control thresholds, aggregation behavior, and visual treatment for this signal.</p>
                <div className="space-y-ds-4">
                  <ThresholdEditor summary={thresholdSummary} onOpen={onOpenThreshold} />
                  {showAggregatorSelector ? (
                    <div>
                      <label className="mb-ds-2 block form-label">
                        Aggregators
                      </label>
                      <AggregatorSelect
                        aria-label={`Aggregators for ${row.name || "channel"}`}
                        value={row.aggregations}
                        onChange={(next) =>
                          onUpdate((c) => ({
                            ...c,
                            aggregations: sanitizeAggregationsForDataType(c.dataType, next),
                          }))
                        }
                      />
                      <p className="mt-ds-2 text-[11px] leading-5 text-muted">
                        Allowed: {aggregationOptions.map((aggregation) => aggregation.replace("_", " ")).join(", ")}
                      </p>
                    </div>
                  ) : (
                    <div className="rounded-control border border-card-border bg-surface-primary px-ds-3 py-ds-3 text-caption text-muted">
                      {row.dataType === "bool"
                        ? "Boolean channels do not use numeric aggregators. They are handled as state signals across live views, alerts, and dashboards."
                        : "String channels do not use numeric aggregators. They are shown as latest text values or in tables."}
                    </div>
                  )}
                  <GraphTypeSelector
                    value={row.graphType}
                    onChange={(g) => onUpdate((c) => ({ ...c, graphType: g }))}
                    options={graphOptions}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Advanced */}
          <div className="mt-ds-6 border-t border-dashed border-card-border pt-ds-4">
            <button
              type="button"
              onClick={onToggleAdvanced}
              className="flex w-full items-center gap-ds-2 rounded-control border border-card-border bg-surface-secondary px-ds-3 py-ds-3 text-left text-body font-medium text-foreground transition-colors hover:bg-surface-primary"
            >
              <ChevronRight
                className={cn(
                  "h-4 w-4 shrink-0 text-muted transition-transform duration-200 ease-out",
                  advancedOpen && "rotate-90",
                )}
                aria-hidden
              />
              <span>Advanced</span>
              <span className="ml-1 text-caption font-normal text-muted">formula, precision, validation details</span>
            </button>

            {advancedOpen ? (
              <div className="mt-ds-3 space-y-ds-4 rounded-[20px] border border-card-border bg-muted-bg/30 px-ds-4 py-ds-4 dark:bg-muted-bg/15">
                {row.dataType === "float" ? (
                  <div className="flex flex-wrap items-center justify-between gap-ds-3 rounded-control border border-card-border bg-surface-primary px-ds-3 py-ds-3">
                    <div>
                      <p className="form-label">Floating-point precision</p>
                      <p className="mt-ds-1 text-[11px] leading-5 text-muted">Adjust how many decimal places operators should see by default.</p>
                    </div>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={onOpenPrecision}>
                      Configure precision…
                    </button>
                  </div>
                ) : null}
                {row.dataType === "formula" ? (
                  <div className="flex flex-wrap items-center justify-between gap-ds-3 rounded-control border border-card-border bg-surface-primary px-ds-3 py-ds-3">
                    <div>
                      <p className="form-label">Formula editor</p>
                      <p className="mt-ds-1 text-[11px] leading-5 text-muted">Define how this channel is computed from other device signals.</p>
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm !px-2"
                      onClick={onOpenFormula}
                      title="Edit formula"
                      aria-label="Edit formula"
                    >
                      <Calculator className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                ) : null}
                <div>
                  <p className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">Validation</p>
                  <p className="mt-ds-1 text-caption leading-relaxed text-muted">
                    Thresholds, warnings, and type-specific rules are edited in <strong className="font-medium text-foreground/90">Logic → Thresholds</strong> above.
                  </p>
                </div>
                <div>
                  <p className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">Debug preview</p>
                  <div className="mt-ds-2 rounded-control border border-card-border bg-surface-primary px-ds-3 py-ds-3 text-caption text-foreground shadow-none">
                    <div>
                      <span className="text-muted">{keyLocked ? "payload key" : "generated key"}</span>{" "}
                      <code className="text-foreground">{row.channelKey}</code>
                    </div>
                    {row.dataType === "formula" && row.formula ? (
                      <div className="mt-ds-1">
                        <span className="text-muted">expr</span>{" "}
                        <code className="text-foreground">{formatFormulaExpression(row.formula)}</code>
                      </div>
                    ) : row.dataType === "formula" ? (
                      <p className="mt-ds-1 text-muted">Formula not configured.</p>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </article>
  );
}
